import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, CheckCheck, X, RotateCcw, Pencil, Lock } from 'lucide-react';
import { TableRow, TableCell } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip';
import { cn } from '../../../lib/utils';
import { formatCents, dollarsToCents, centsToDollarsInput, STATUS_BADGE } from './format';

// Shared by the anniversary and Christmas bonus views: both moderate the same way.

// PlayerCell: the email first, the user_id under it, both linking to the player page. Without an
// email the user_id is the headline.
export function PlayerCell({ row }) {
  if (!row.user_id) return '—';
  return (
    <Link to={`/player/${row.user_id}?from=dashboard`} className="group block">
      {row.email ? (
        <>
          <div className="max-w-[240px] truncate font-bold text-ink group-hover:text-cta" title={row.email}>{row.email}</div>
          <div className="text-xs font-medium text-ink-dim group-hover:text-cta">{row.user_id}</div>
        </>
      ) : (
        <span className="font-bold text-ink group-hover:text-cta">{row.user_id}</span>
      )}
    </Link>
  );
}

// StatusBadge: booked bonuses (a record in the bonus history table) show "Will be issued on <day>"
// until that day and "Issued" from then on; hovering shows the day and amount. fallbackDay is the
// bonus's own date, used when there is no history day.
export function StatusBadge({ row, fallbackDay }) {
  const s = STATUS_BADGE[row.status] || { label: row.status || '—', variant: 'outline' };
  const day = row.issued_day || fallbackDay;
  const label = row.status === 'scheduled' && day ? `Will be issued on ${day}` : s.label;
  const title = row.issued ? `${row.status === 'scheduled' ? 'Will be issued' : 'Issued'} ${day || ''} · ${formatCents(row.issued_amount_usd_cents)}` : undefined;
  return <Badge variant={s.variant} title={title} className="whitespace-nowrap">{label}</Badge>;
}

// ModerationEditor is the expanded sub-row: override amount + note, then override / reject / reset.
export function ModerationEditor({ row, colSpan, busy, onModerate, onClose }) {
  const [amount, setAmount] = useState(centsToDollarsInput(row.override_amount_usd_cents ?? row.proposed_bonus_amount_usd_cents));
  const [note, setNote] = useState(row.moderation_note || '');
  const cents = dollarsToCents(amount);
  const amountInvalid = amount !== '' && cents === null;

  const submit = (action) => onModerate(row, { action, amount_usd_cents: action === 'override' ? cents : null, note });

  return (
    <TableRow className="bg-surface-2/60 hover:bg-surface-2/60">
      <TableCell colSpan={colSpan}>
        <div className="flex flex-wrap items-end gap-3 py-1">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-dim">
            Override amount (USD)
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && cents) submit('override'); }}
              inputMode="decimal"
              placeholder={centsToDollarsInput(row.proposed_bonus_amount_usd_cents)}
              className={cn('h-8 w-32 text-sm', amountInvalid && 'border-negative')}
              autoFocus
            />
          </label>
          <label className="flex flex-1 min-w-[220px] flex-col gap-1 text-xs font-medium text-ink-dim">
            Note (optional)
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="Why the change?"
              className="h-8 text-sm"
            />
          </label>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !cents} onClick={() => submit('override')}>
              <Check className="mr-1 h-3.5 w-3.5" /> Save override
            </Button>
            {row.status !== 'approved' && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => submit('approve')}
                title="Sign off the proposed amount as it is">
                <CheckCheck className="mr-1 h-3.5 w-3.5" /> Approve proposed
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => submit('reject')}
              className="text-negative hover:text-negative">
              <X className="mr-1 h-3.5 w-3.5" /> Reject
            </Button>
            {row.status !== 'pending' && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => submit('reset')}
                title="Back to pending: the proposed amount will be issued">
                <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset to proposed
              </Button>
            )}
            <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>Cancel</Button>
          </div>
        </div>
        {amountInvalid && <p className="mt-1 text-xs font-medium text-negative">Enter an amount greater than 0 (max 2 decimals).</p>}
      </TableCell>
    </TableRow>
  );
}

// RowActions is the last cell: one-click Approve for pending rows plus Moderate while editable,
// else a lock with lockText explaining why. Approving keeps the row's existing note.
export function RowActions({ row, editing, busy, onEdit, onClose, onModerate, lockText }) {
  if (!row.editable) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="ml-auto flex rounded p-1 text-ink-mute hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cta"
            aria-label={lockText}>
            <Lock className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">{lockText}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <div className="flex justify-end gap-1">
      {row.status === 'pending' && (
        <Button size="sm" variant="ghost" disabled={busy} title="Approve the proposed amount"
          onClick={() => onModerate(row, { action: 'approve', amount_usd_cents: null, note: row.moderation_note || '' })}
          className="text-brand-green hover:text-brand-green">
          <Check className="mr-1 h-3.5 w-3.5" /> Approve
        </Button>
      )}
      <Button size="sm" variant={editing ? 'secondary' : 'ghost'} disabled={busy} onClick={editing ? onClose : onEdit}>
        <Pencil className="mr-1 h-3.5 w-3.5" /> Moderate
      </Button>
    </div>
  );
}

const PLAYER_SEARCH_DEBOUNCE_MS = 350;

// PlayerSearch is the "ID or email" filter box. It reports the text only once typing pauses, so
// an email search does not query the warehouse per keystroke; a reset from the parent (value
// changing underneath) is mirrored back into the box.
export function PlayerSearch({ value, onChange }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  useEffect(() => {
    if (text === value) return undefined;
    const id = setTimeout(() => onChange(text), PLAYER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [text, value, onChange]);

  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink-dim">
      Player
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="ID or email"
        className="h-9 w-52 text-sm" />
    </label>
  );
}
